import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm } from 'components/TableSettings';
import type { WorkOrderSeasonData, WorkOrderTotalData, WorkOrderVersionSeasonData } from 'gql/graphql';
import { useSelection } from 'hooks/useSelection';

import type { ColDef } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';

import { cellClass, getFormattedValue, getSeasonColumns } from '../helpers';

type TColumns = ColDef<WorkOrderTotalData>;

interface IUseOrderSummaryTableColumns {
    instanceId?: string;
    costRounding: number;
    workOrderSeasons: (WorkOrderSeasonData | WorkOrderVersionSeasonData)[];
}

export const useOrderSummaryTableColumns = (props: IUseOrderSummaryTableColumns) => {
    const { costRounding, instanceId, workOrderSeasons: seasons } = props;
    const [selection, , , setSelection] = useSelection(`summary_${instanceId}`);

    const columns = useMemo<TColumns[]>(
        () => [
            { headerName: 'Наименование', field: 'name', flex: 2, minWidth: 250, cellClass },
            {
                headerName: 'План по НЗ',
                field: 'totalPlanCost',
                flex: 1,
                minWidth: 200,
                type: 'numericColumn',
                valueFormatter: getFormattedValue(costRounding),
                cellClass,
            },
            ...getSeasonColumns({
                valueKey: 'planCost',
                costRounding,
                seasons,
                name: 'План',
            }),
            {
                headerName: 'Выполнено по НЗ',
                field: 'totalCompletedCost',
                flex: 1,
                minWidth: 200,
                type: 'numericColumn',
                valueFormatter: getFormattedValue(costRounding),
                cellClass,
            },
            ...getSeasonColumns({
                valueKey: 'completedCost',
                costRounding,
                seasons,
                name: 'Выполнено',
            }),
            {
                headerName: 'Остаток по НЗ',
                field: 'totalRemainderCost',
                flex: 1,
                minWidth: 200,
                type: 'numericColumn',
                valueFormatter: getFormattedValue(costRounding),
                cellClass,
            },
        ],
        [costRounding, seasons],
    );
    const isColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);

    const fullSetSettingsColumns = useMemo(
        () =>
            columns.reduce((acc: ColDef[], column, idx) => {
                const newColumn = { ...column } as ColDef & { disabled: boolean };

                if (idx < 1) {
                    newColumn.disabled = true;
                }

                acc.push(newColumn);

                return acc;
            }, []),
        [columns],
    );

    const tableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                settingColumns: fullSetSettingsColumns,
                incomingColumns: visible,
                setSelection,
                setPinned: () => undefined,
            });
        },
        [fullSetSettingsColumns, setSelection],
    );

    const fullSetColumns = useMemo(() => {
        const fullSetColumnsWithPins = getColumnsWithPinnedProp({ columns, pinnedItems: {} });

        return fullSetColumnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [columns, isColumnVisible]);

    return {
        columns: fullSetColumns,
        fullSetSettingsColumns,
        isColumnVisible,
        tableSettingsConfirm,
    };
};
